import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ApiChat from '../adapters/apiChat';

const ChatHistoryContext = createContext(null);

/**
 * No-op stand-in used when there is no provider above.
 *
 * ChatComponent is rendered on three pages, and only the /ask page wraps it in
 * a ChatHistoryProvider. The hook used to throw in the other two, so
 * ChatComponent called it inside a try/catch — a conditional hook that only
 * worked by accident. Returning an inert value instead makes the sidebar
 * genuinely optional.
 */
const NO_HISTORY = {
  chatHistory: [],
  addChatEntry: () => {},
  clearHistory: () => {},
  isAvailable: false,
  isLoading: false,
  error: null,
};

export const useChatHistory = () => useContext(ChatHistoryContext) || NO_HISTORY;

/**
 * Recent questions, served from the API rather than held in memory.
 *
 * This used to be a bare useState([]), which meant the rail emptied on every
 * reload and on every navigation away from /ask, because the provider is
 * mounted inside that route rather than above the router.
 *
 * The store is DynamoDB, keyed on the verified subject from the caller's token,
 * so history follows the user across devices and browsers instead of being
 * stranded in one profile's localStorage. Entries are the thread records that
 * already existed for AgentCore session mapping, so selecting one can resume
 * the conversation rather than re-ask the question cold.
 */
export const ChatHistoryProvider = ({ children }) => {
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiChat = useRef(new ApiChat());

  // Guards against a setState after unmount, which React warns about and which
  // is easy to hit here: the rail lives on a route the user can leave while the
  // request is still in flight.
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const refresh = useCallback(async () => {
    try {
      const threads = await apiChat.current.getHistory();
      if (!alive.current) return;
      setChatHistory(
        threads.map((t) => ({
          threadId: t.thread_id,
          text: t.title,
          // Milliseconds, because the rail formats these with the Date API and
          // the API speaks Unix seconds.
          timestamp: (t.last_asked_at ?? t.created_at ?? 0) * 1000,
        }))
      );
      setError(null);
    } catch (err) {
      if (!alive.current) return;
      // A signed-out user gets a 401 here, which is not worth shouting about:
      // the rail simply has nothing to show.
      setError(err.message ?? 'Could not load recent questions');
    } finally {
      if (alive.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(
    () => ({
      chatHistory,
      isLoading,
      error,
      isAvailable: true,

      /**
       * Optimistic insert, so the rail updates the moment a question is
       * answered instead of waiting for a refetch. The server is still the
       * source of truth: the entry is replaced on the next refresh, and
       * de-duplicated by threadId here so a follow-up in the same conversation
       * moves the existing row to the top rather than adding a second one.
       */
      addChatEntry: (entry) =>
        setChatHistory((prev) => {
          const withoutThread = entry.threadId
            ? prev.filter((e) => e.threadId !== entry.threadId)
            : prev;
          return [entry, ...withoutThread];
        }),

      // Local only, deliberately. Clearing the rail is a display preference,
      // and wiring it to a DELETE would throw away resumable conversations and
      // the thread mapping behind them. A reload brings them back.
      clearHistory: () => setChatHistory([]),

      refresh,
    }),
    [chatHistory, isLoading, error, refresh]
  );

  return (
    <ChatHistoryContext.Provider value={value}>
      {children}
    </ChatHistoryContext.Provider>
  );
};
